'use client';

import {
  Box,
  Button,
  Flex,
  FormHelperText,
  freddyTheme,
  Typography
} from '@coolblue-development/freddy';
import selectRemoveUploadTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/selectRemoveUploadTrigger';
import selectUploadTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/selectUploadTrigger';
import showUploadMessageTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationForm/showUploadMessageTrigger';
import { Paperclip, Upload } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { useTranslation } from '@i18n/useTranslation';
import { DocumentType, Rules } from '@sharedTypes';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormLabelWithInfo } from '../FormLabelWithInfo';

type Props = {
  name: string;
  documentType: DocumentType;
  label: string;
  buttonText: string;
  rules?: Rules;
  info?: string;
};

export const FileUploadHelper = ({ name, documentType, label, buttonText, rules, info }: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const acceptedFileType = rules?.fileType?.value ?? 'application/pdf';
  const clientLogger = useClientLogger();
  const { t } = useTranslation('vacancyDetail.applicationForm.questions.uploadFile');

  const {
    register,
    setError,
    setValue,
    formState: { errors }
  } = useFormContext();

  register(name, {
    required: rules?.required?.message,
    validate: {
      fileType: file =>
        validateFileType(file) ? true : (rules?.fileType?.message ?? t('invalidFileType'))
    }
  });

  const { trackEvent } = useAnalytics();

  const subFeature = documentType === DocumentType.Resume ? 'cv' : 'motivation_letter';
  const trackUploadMessage = (content: string) => {
    trackEvent(
      showUploadMessageTrigger({
        sub_feature: subFeature,
        content
      })
    );
  };

  const errorMessage = errors[name]?.message;
  if (errorMessage) trackUploadMessage(errorMessage.toString());

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const validateFileType = (file: string | undefined) =>
    !!file?.startsWith(`data:${acceptedFileType};base64`);

  const validateAndSetSelectedFile = (file: File) => {
    const maxFileSize = rules?.maxFileSize?.value;
    if (maxFileSize && file.size > maxFileSize) {
      setError(name, {
        type: 'manual',
        message: rules?.maxFileSize?.message
      });

      return;
    }

    toBase64(file).then(value => {
      clientLogger.info('File converted to base64', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        base64Preview: value ? value.toString().substring(0, 28) + '...' : null
      });

      if (validateFileType(value?.toString())) {
        trackUploadMessage('success');
      }
      setValue(name, value, { shouldValidate: true });
    });
    setSelectedFile(file);
  };

  const handleUploadClick = () => {
    document.getElementById(name)?.click();
    selectUploadTrigger({ sub_feature: subFeature });
  };

  return (
    <Flex direction='column' alignItems='baseline' gap={200}>
      <FormLabelWithInfo label={label} info={info} />

      {!selectedFile && (
        <Button
          component='div'
          variant='secondary'
          fullWidthUntil='md'
          startIcon={<Upload />}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleUploadClick();
            }
          }}
          onClick={handleUploadClick}
        >
          {buttonText}
          <input
            type='file'
            id={name}
            accept={acceptedFileType}
            onChange={e => {
              if (e.target.files) validateAndSetSelectedFile(e.target.files[0]);
            }}
            hidden
          />
        </Button>
      )}

      {selectedFile && (
        <Flex
          justifyContent='space-between'
          alignItems='center'
          gap={200}
          border='1px solid'
          borderColor='base'
          borderRadius={100}
          padding={200}
          sx={{ width: '100%' }}
        >
          <Paperclip color={freddyTheme.color.icon.subtle} />
          <Typography flexGrow={2} color='subtle' marginBottom={0} truncateLines={1}>
            {selectedFile.name}
          </Typography>
          <Box
            component='button'
            color='interactiveBase'
            onClick={() => {
              setSelectedFile(null);
              setValue(name, '', { shouldValidate: true });
              trackEvent(selectRemoveUploadTrigger({ sub_feature: subFeature }));
            }}
            sx={{ all: 'unset', '&:hover': { cursor: 'pointer' } }}
          >
            {t('deleteButton')}
          </Box>
        </Flex>
      )}

      {errorMessage && (
        <FormHelperText validation='error'>{errorMessage.toString()}</FormHelperText>
      )}
    </Flex>
  );
};
